<script>
    import InfiniteScroll from "svelte-infinite-scroll";
    import {slide} from 'svelte/transition';

    import {onMount} from 'svelte';
    import {browser} from '$app/environment';
    import {goto, beforeNavigate, afterNavigate, invalidate} from '$app/navigation';

    import {request, query, total, docs, categories, start_date, end_date, start_date_string, end_date_string, size, has_more, clicked_date, clicked_category, current_dossier_id, doc_feed_scroll_position, last_refresh, total_new_docs} from '$lib/store.js';
    import {post_call, process_nested_categories, clear_clicked_cat_and_date} from '$lib/utils.js';

    import Head from '$lib/Head.svelte';
    import Search from './Search.svelte';
    import CreateDossierButton from './CreateDossierButton.svelte';
    import TryPro from './TryPro.svelte';
    import GetHelp from './GetHelp.svelte';
    import Dossiers from './Dossiers.svelte';
    import DocFilters from './DocFilters.svelte';
    import filter_icon from './filter.svg';

    export let data;

    // Stores the new batch/page of documents coming from the infinite scroll
    let new_docs = [];

    // Stores the width of the window
    let innerWidth = 0;
    let offcanvas;
    let opened_canvas;
    $: {
        // If the filters offcanvas is open (on small screens), close it when
        // the window is resized to lg or larger
        if (innerWidth > 991) {
            opened_canvas = bootstrap.Offcanvas.getInstance(offcanvas);
            if (opened_canvas) {
                opened_canvas.hide();
            }
        }
    }

    // Fetch the next page of documents when scrolling
    async function fetch_scroll_data() {
        const response = await post_call($request);
        new_docs = await response.result.hits;
        // Set has_more to false if we collected the last batch/page of
        // documents; this will stop the infinite scroll
        if ($request.body.from + $size >= $total) {
            $has_more = false;
        }
    };

    // Always append new_docs to docs which automatically renders them on the
    // page
    $: {
        if ($docs) {
            $docs = [
                ...$docs,
                ...new_docs
            ];
        }

        // Clear new_docs after its was added to $docs
        new_docs = [];
    }

    // Rerun the +page.js load function when the user enters/changes a date
    // (clicking already worked, but this is needed for manually
    // entered/typed dates)
    $: {
        $start_date_string;
        $end_date_string;
        process_date();
    }

    // These lines are in a separate function otherwise changes of
    // $clicked_date would trigger the reactivity above
    function process_date() {
        // Clear any previous 'clicked_*' states
        clear_clicked_cat_and_date();
        $clicked_date = true;
        // Check if the event element has a parent with the class
        // 'date-time-picker' which means that the user selected a new date
        // using the picker or 'date-time-field' which means the input field
        // was changed directly by the user (i.e typing/clearing the input
        // field)
        if (browser && event?.srcElement.closest('.date-time-picker, .date-time-field')) {
            // Rerun the load function
            invalidate('app:page');
        }
    }

    // Rerun the +page.js load function when the user (un)checks a categories
    // facet
    $: {
        $categories;
        // Check if the user clicked a checkbox (include 'aside' to avoid
        // running when the add dossier modal categories are clicked)
        if (browser && event?.srcElement.closest('aside .checkboxes, .offcanvas .checkboxes')) {
            process_category();
        }
    }

    // These lines are in a separate function otherwise changes of
    // $clicked_category, $category, $current_dossier_id and $query would
    // trigger the reactivity above
    function process_category() {
        // Clear any previous 'clicked_*' states
        clear_clicked_cat_and_date();
        $clicked_category = true;
        process_nested_categories($categories);
        // When a dossier (except 'all') was selected and the user clicked a
        // category, then we are leaving the dossier and moving to a 'zoek' URL
        if ($current_dossier_id && $current_dossier_id !== 'all') {
            goto(`/app?zoek=${$query}`);
        // Otherwise don't change the URL and simply rerun the load function
        } else {
            // Rerun the load function
            invalidate('app:page');
        }
    }

    // Call that is run every 30 seconds to see if there are new documents
    async function check_new_docs() {
        // Reuse the current request
        let _request = structuredClone($request);

        // Check for new documents since the last page load/change
        _request.body['dateRange'] = {from: $last_refresh, to: $last_refresh.replace(/..:..:.*/, '21:59:59.999Z')};
        // This makes sure that 'hits' are not returned as we only need 'total'
        _request.body['search_type'] = 'count';
        // These parameters are not needed
        delete _request.body['size'];
        delete _request.body['from'];

        let _response = await post_call(_request);

        if (_response?.result?.total) {
            $total_new_docs = _response.result.total;
        }
    }

    let interval_id = undefined;

	onMount(() => {
        offcanvas = document.getElementById('offcanvas');
    });

    // When returning to the feed
    afterNavigate(({to}) => {
        // Scroll to the previous scrollY position (if not available it
        // defaults to 0); this works both with the back button and 'Meer
        // documenten' button
        window.scrollTo(0, $doc_feed_scroll_position);

        // Set an interval to run check_new_docs every 30 seconds
        interval_id = setInterval(check_new_docs, 30000);
    });

    // When navigating away from the feed
    beforeNavigate(() => {
        // Save the current scrollY position
        $doc_feed_scroll_position = window.scrollY;

        // Clear the interval
        clearInterval(interval_id);
        // Clear any previous 'clicked_*' states
        clear_clicked_cat_and_date();
    });
</script>

<svelte:window bind:innerWidth/>

<Head title="{$total_new_docs > 0 ? `(${$total_new_docs}) ` : ''}Politieke monitor en zoekmachine voor Binnenhof en EU | 1848.nl"/>

<h1 class="visually-hidden">Documenten feed</h1>
<div class="col-lg-7 col-xxl-8">
    <h2 class="visually-hidden">Feed</h2>
    <Search location='/app'/>

    <button id="filter-button" class="d-lg-none btn btn-medium bg-gold" type="button" data-bs-toggle="offcanvas" data-bs-target="#offcanvas" aria-controls="offcanvas">
        Filter <img style="margin-left:10px;" src={filter_icon} alt="" width="25" height="22.4333">
    </button>

    <CreateDossierButton display='d-lg-none' --margin='0 0 10px 0'/>

    {#if data.logged_in}
        <Dossiers display='d-lg-none' show='' location='/app'/>
    {/if}

    {#if $total_new_docs}
        <div class="d-flex" transition:slide|local>
            <button id="new-docs-button" type="button" class="flex-grow-1 bg-light-blue5" on:click={() => {$start_date = $end_date = null; invalidate('app:page');}}>
                Er {$total_new_docs === 1 ? 'is 1 nieuw document' : `zijn ${$total_new_docs} nieuwe documenten`} beschikbaar
            </button>
        </div>
    {/if}

    <div id="feed">
        {#if $docs}
            {#each $docs as doc, i}
                {#if !data.logged_in}
                    {#if i == 1}
                        <TryPro display='d-lg-none' --margin_bottom={'30px'}/>
                    {/if}

                    {#if i == 2}
                        <GetHelp display='d-lg-none' --margin_bottom={'30px'}/>
                    {/if}
                {/if}

                <div id={doc.doc_type + '_' + doc.doc_id} class="bg-white feed-doc">
                    <a href={"/document/" + doc.doc_type + "/" + doc.doc_id}><h3>{@html doc.title}</h3></a>
                    <p class="doc-date">{new Date(doc.publication_date).toLocaleDateString('nl-NL', {timeZone: 'Europe/Amsterdam', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})}</p>
                    <p>{@html doc.snippet}</p>
                    <hr class="bg-light-blue5">
                    <p class="doc-footer"><b>Bron:</b> {doc.source_name}</p>
                    <p class="doc-footer"><b>Categorie:</b> {doc.categories}</p>
                </div>
            {/each}
        {/if}
        <InfiniteScroll
            hasMore={$has_more}
            threshold=1000
            window=true
            on:loadMore={() => {$request.body.from += $size; fetch_scroll_data();}}/>
    </div>

    {#if !$total}
        <p class="text-center">Deze zoekopdracht leverde geen resultaten op.</p>
    {/if}
</div>

<aside class="d-none d-lg-block offset-lg-1 col-lg-4 col-xxl-3">
    {#if data.logged_in}
        <Dossiers show='show' allow_modals=true location='/app'/>
    {:else}
        <TryPro --margin_bottom={'20px'}/>
        <GetHelp --margin_bottom={'37px'}/>
    {/if}

    <h2 class="visually-hidden">Filters</h2>
    <DocFilters filter_location=':aside'/>
</aside>

<div class="offcanvas offcanvas-start" tabindex="-1" id="offcanvas" aria-labelledby="offcanvas-label">
    <div class="offcanvas-header justify-content-end">
        <h2 id="offcanvas-label" class="visually-hidden">Filters</h2>
        <button type="button" class="btn-close text-end" data-bs-dismiss="offcanvas" aria-label="Sluiten"></button>
    </div>
    <div class="offcanvas-body">
        <DocFilters filter_location=':offcanvas'/>
    </div>
</div>

<style lang="scss">
    #new-docs-button {
        border: none;
        padding-top: 13px;
        padding-bottom: 13px;
        text-align: center;

        &:hover {
            text-decoration: underline;
        }
    }

    #feed {
        margin-top: 20px;
    }

    .feed-doc {
        padding: 50px 57px 44px 30px;
        margin-bottom: 30px;

        &:last-child {
            margin-bottom: 0;
        }

        .doc-date::first-letter {
            text-transform: capitalize;
        }

        .doc-date {
            font-size: 14px;
            line-height: 20.8px;
            font-weight: 600;
            color: var(--dark-blue-color);
        }

        h3 {
            font-size: 30px !important;
            margin-bottom: 20px;
        }

        a {
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }

            &:visited {
                color: revert !important;
            }
        }

        :global(em) {
            font-weight: 600;
            font-style: normal;
            color: var(--dark-blue-color);
        }

        .doc-footer {
            color: var(--dark-blue-color);
            font-size: 16px;
            line-height: 20.8px;

            &::not(:last-child) {
                margin-bottom: 26px;
            }

            &:last-child {
                margin-bottom: 0;
            }

            b {
                font-weight: 600;
            }
        }
    }

    #filter-button {
        margin-bottom: 10px;
        padding: 8px 14px 8.5px 22px;
        line-height: 22px;
        border-radius: 100px;
        border-width: 0;
        box-shadow: none;
        font-weight: bold;
    }

    :global(.offcanvas-body) {
        padding-top: 0;

        :global(ul:last-child) {
            margin-bottom: 0 !important;
        }
    }

    @media (max-width: 575px) {
        .feed-doc {
            padding: 20px;

            h3 {
                font-size: 28px !important;
                line-height: 32px !important;
            }
        }
    }
</style>
